import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Package, ShoppingCart, Users, FileText, Bell, LogOut, Settings, Receipt, MapPin, Menu, X, MessageSquare } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { fetchNotifications(); }, []);
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const fetchNotifications = async () => {
    try { const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, { withCredentials: true }); setNotifications(data); } catch {}
  };
  const markAsRead = async (id) => {
    try { await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${id}/read`, {}, { withCredentials: true }); fetchNotifications(); } catch {}
  };
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const allNav = [
    ...(user?.role === 'admin' ? [{ name: 'Dashboard', path: '/', icon: LayoutDashboard }] : []),
    { name: 'Bags', path: '/bags', icon: Package },
    { name: 'Tracking', path: '/bag-tracking', icon: MapPin },
    { name: 'Sales', path: '/sales', icon: ShoppingCart },
    { name: 'Parties', path: '/parties', icon: Users },
    { name: 'Expenses', path: '/expenses', icon: Receipt },
    ...(user?.role === 'admin' ? [{ name: 'Reports', path: '/reports', icon: FileText }] : []),
    ...(user?.role === 'admin' ? [{ name: 'Settings', path: '/settings', icon: Settings }] : []),
    { name: 'Contact Us', path: '/contact', icon: MessageSquare },
  ];

  // Bottom mobile nav: show first 5 items
  const mobileBottomNav = allNav.slice(0, 5);
  const mobileOverflowNav = allNav.slice(5);

  return (
    <div className="min-h-screen bg-[#F7F5F0] pb-16 md:pb-0">
      {/* Header - sticky */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E4DFD3] px-3 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm sm:text-lg">M</span>
            </div>
            <h1 className="text-sm sm:text-xl font-extrabold text-[#1A2620] font-['Manrope'] truncate" data-testid="app-title">
              Mushroom Farm
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Notifications */}
            <div className="relative">
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 hover:bg-[#F7F5F0] rounded-md" data-testid="notifications-button">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-[#2C4A3B]" />
                {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-[#D05A4A] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center" data-testid="notification-badge">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white border border-[#E4DFD3] rounded-lg shadow-lg max-h-96 overflow-y-auto z-50" data-testid="notifications-panel">
                  <div className="p-3 border-b border-[#E4DFD3]"><h3 className="font-semibold text-[#1A2620] text-sm">Notifications</h3></div>
                  {notifications.length === 0 ? <div className="p-4 text-center text-[#5C6B63] text-sm">None</div> :
                    notifications.map(n => (
                      <div key={n.id} className={`p-3 border-b border-[#E4DFD3] last:border-0 cursor-pointer hover:bg-[#F7F5F0] ${!n.is_read ? 'bg-[#EDF5EE]' : ''}`} onClick={() => markAsRead(n.id)} data-testid={`notification-item-${n.id}`}>
                        <h4 className="font-medium text-xs text-[#1A2620]">{n.title}</h4>
                        <p className="text-xs text-[#5C6B63] mt-0.5">{n.message}</p>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
            {/* User */}
            <div className="hidden sm:flex items-center gap-2">
              <div className="text-right">
                <p className="text-sm font-medium text-[#1A2620]" data-testid="user-name">{user.name}</p>
                <p className="text-xs text-[#5C6B63] uppercase tracking-wider" data-testid="user-role">{user.role}</p>
              </div>
            </div>
            <button onClick={logout} className="p-2 hover:bg-[#FAEDED] rounded-md" data-testid="logout-button">
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-[#D05A4A]" />
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Navigation - hidden on mobile */}
      <nav className="hidden md:block bg-white border-b border-[#E4DFD3] px-6 overflow-x-auto">
        <div className="flex gap-1">
          {allNav.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} className={`flex items-center gap-2 px-3 py-3 border-b-2 transition-colors whitespace-nowrap ${isActive ? 'border-[#2C4A3B] text-[#2C4A3B]' : 'border-transparent text-[#5C6B63] hover:text-[#2C4A3B]'}`} data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}>
                <Icon className="w-4 h-4" /><span className="text-sm font-medium">{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-3 sm:p-6 lg:p-8">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E4DFD3] z-50 safe-area-pb">
        <div className="flex justify-around items-center">
          {mobileBottomNav.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} className={`flex flex-col items-center py-2 px-1 min-w-0 flex-1 ${isActive ? 'text-[#2C4A3B]' : 'text-[#5C6B63]'}`} data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-0.5 truncate font-medium">{item.name}</span>
              </Link>
            );
          })}
          {mobileOverflowNav.length > 0 && (
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`flex flex-col items-center py-2 px-1 min-w-0 flex-1 ${mobileMenuOpen ? 'text-[#2C4A3B]' : 'text-[#5C6B63]'}`} data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              <span className="text-[10px] mt-0.5 font-medium">More</span>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile overflow menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed bottom-14 left-0 right-0 bg-white border-t border-[#E4DFD3] z-40 shadow-lg" data-testid="mobile-overflow-menu">
          {mobileOverflowNav.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-6 py-3 border-b border-[#E4DFD3] last:border-0 ${isActive ? 'text-[#2C4A3B] bg-[#EDF5EE]' : 'text-[#5C6B63]'}`}>
                <Icon className="w-5 h-5" /><span className="text-sm font-medium">{item.name}</span>
              </Link>
            );
          })}
          <div className="flex items-center gap-3 px-6 py-3 text-[#5C6B63]">
            <span className="text-sm">{user.name} ({user.role})</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
